import { isHexColor } from '@/utils/isHexColor';
import unflatten from '@/utils/unfletten';

const DEFAULT_COLOR = 'var(--color-principal)';

const asArray = (value) => Array.isArray(value) ? value : [];

export const resolveSheetColors = (tenantSettings = {}) => {
    const settings = unflatten(tenantSettings ?? {});
    return settings?.style?.color?.sheet ?? {};
};

export const toCssColorValue = (color, fallbackColor = DEFAULT_COLOR) => {
    if (!color) return fallbackColor;

    const value = String(color).trim();
    if (!value) return fallbackColor;

    if (
        isHexColor(value)
        || value.startsWith('var(')
        || value.startsWith('rgb(')
        || value.startsWith('rgba(')
        || value.startsWith('hsl(')
        || value.startsWith('hsla(')
    ) {
        return value;
    }

    return `var(--color-${value}, ${fallbackColor})`;
};

export const resolveLinkTypeColor = (linkType, tenantSettings = {}, fallbackColor = DEFAULT_COLOR) => {
    const sheetColors = resolveSheetColors(tenantSettings);
    const sheets = asArray(linkType?.sheets);

    for (const sheet of sheets) {
        if (sheet?.name && sheetColors[sheet.name]) {
            return sheetColors[sheet.name];
        }
    }

    for (const sheet of sheets) {
        if (sheet?.color) {
            return sheet.color;
        }
    }

    return linkType?.color ?? fallbackColor;
};

const rawTypesFromMeta = (meta = {}) => {
    if (Array.isArray(meta.types) && meta.types.length) {
        return meta.types;
    }

    return Object.entries(meta.counts_by_type ?? {}).map(([label, count]) => ({
        key: label,
        label,
        count,
        sheets: [],
    }));
};

const mergeSheets = (targetSheets, nextSheets) => {
    const seen = new Set(targetSheets.map((sheet) => sheet?.name ?? sheet?.label));

    asArray(nextSheets).forEach((sheet) => {
        const key = sheet?.name ?? sheet?.label;
        if (!key || seen.has(key)) return;
        seen.add(key);
        targetSheets.push(sheet);
    });
};

export const normalizeSheetLinkTypes = (meta = {}, tenantSettings = {}, options = {}) => {
    const fallbackColor = options.fallbackColor ?? DEFAULT_COLOR;
    const byKey = new Map();

    rawTypesFromMeta(meta).forEach((type) => {
        const label = type?.label ?? type?.key;
        const key = type?.key ?? label;
        if (!key || !label) return;

        const count = Number(type?.count ?? meta.counts_by_type?.[key] ?? meta.counts_by_type?.[label] ?? 0);
        if (!Number.isFinite(count) || count <= 0) return;

        if (!byKey.has(key)) {
            byKey.set(key, {
                key,
                label,
                count: 0,
                sheets: [],
            });
        }

        const entry = byKey.get(key);
        entry.count += count;
        mergeSheets(entry.sheets, type?.sheets);
    });

    return Array.from(byKey.values())
        .map((item) => {
            const color = resolveLinkTypeColor(item, tenantSettings, fallbackColor);

            return {
                ...item,
                color,
                colorValue: toCssColorValue(color, fallbackColor),
            };
        })
        .sort((left, right) => left.label.localeCompare(right.label, 'it', { sensitivity: 'base' }));
};
